/**
 ShapeDiver 3D Viewer Application

 OBJLoader.js

 Content:
   Loader used to load obj files and convert them into THREE.Object3D objects.

  Copyright (c) 2018 ShapeDiver GmbH

  @module OBJLoader
 */

var THREE = require('../../externals/three');

var MaterialPropertiesStandard = require('../materials/MaterialPropertiesStandard');

require('../../3d/loaders/MTLLoader');
require('../../3d/loaders/OBJLoader');
require('../../3d/loaders/DDSLoader');

/**
  * Constructor of the singleton OBJLoader class
  */
var OBJLoader = new function () {

  //var that = this;

  // dummy log functionality, will be replaced by the ViewerApp
  this.log = function () {};
  this.warn = function () {};
  this.error = function () {};

  THREE.Loader.Handlers.add( /\.dds$/i, new THREE.DDSLoader() );

  /**
   * Loads a obj file and creates a THREE.Object3D from the content.
   * @param  {String} urlObj - URL of the obj file
   * @param  {String} [urlMtl] - URL of a corresponding mtl file
   * @param  {String} [path] - Optional path to use for loading obj and mtl
   * @param  {String} [texturepath] - Optional path to use for loading textures
   * @param {String} [side='double'] - The sidedness to use for the mesh, one of 'front', 'back', 'double'
   * @param  {THREE.Matrix4[]} [transforms] - Array of transformation matrices to be applied to the resulting mesh
   * @return {Promise<module:OutputVersion~GeometryData[]>} Definition of geometry representing the content of the obj.
   */
  this.loadGeometry = function (urlObj, urlMtl, path, texturepath, side_, transforms) {

    // function which loads MTL files and dependencies in case an mtl url was provided
    let loadMtl = function() {

      // we use a separate loading manager to be able to get informed when all
      // textures have been loaded
      let loadingManager = new THREE.LoadingManager();
      let mtlLoader = new THREE.MTLLoader(loadingManager);
      mtlLoader.setCrossOrigin(true);

      if (path != null) {
        mtlLoader.setPath(path);
      }

      if (texturepath != null) {
        mtlLoader.setTexturePath(texturepath);
      }

      let sidedness = THREE.DoubleSide;
      if (side_ === 'front')
        sidedness = THREE.FrontSide;
      else if (side_ === 'back')
        sidedness = THREE.BackSide;

      let options = {};
      options.wrap = THREE.ClampToEdgeWrapping;
      options.side = sidedness;
      mtlLoader.setMaterialOptions(options);

      // create promise which resolves once we have the material data
      return new Promise(function() {

        let onProgress = function() {
          // here we could send PROCESS messages with a data member of PROCESS_STATUS telling about the progress
          // or just call a callback in the scene manager, which knows the process token anyway
        };
        let onError = function() {
          // TODO: give a warning about missing material data
          loadObj();
        };

        let materialData = null;
        loadingManager.onLoad = function() {
          if (materialData.hasOwnProperty('materialsInfo') && Object.keys(materialData.materialsInfo).length > 0) {
            return loadObj(materialData);
          }
          return loadObj();
        };

        mtlLoader.load( urlMtl, function( materials ) {
          materials.preload();
          materialData = materials;
        }, onProgress, onError);
      });
    };

    // this function downloads the pure mesh data and creates a mesh geometry
    let loadObj = function(materials) {

      return new Promise(function(resolve, reject) {

        let onProgress = function() {
        // FIXME here we could send PROCESS messages with a data member of PROCESS_STATUS telling about the progress
        // or just call a callback in the scene manager, which knows the process token anyway
        };
        let onError = function(err) {
          reject(err);
        };

        let objLoader = new THREE.OBJLoader();
        if (materials != null) {
          objLoader.setMaterials( materials );
        }
        if (path != null) {
          objLoader.setPath( path );
        }

        objLoader.load( urlObj, function ( object ) {

          // sanity checks for transformations
          if (!Array.isArray(transforms) ||
              transforms.length == 0 ||
              !transforms.every(function(m) {return m && m.isMatrix4;})
          ) {
            transforms = [new THREE.Matrix4()];
          }

          let outputGeometry = [];

          object.traverse(function(obj) {

            if (obj.geometry != null) {

              // check if we have texture coordinates
              // check for texture coordinates
              let hasUV = false;
              if (obj.geometry instanceof THREE.BufferGeometry) {
                if (obj.geometry.hasOwnProperty('attributes') && obj.geometry.attributes.hasOwnProperty('uv')) {
                  hasUV = true;
                }
              } else {
                if (obj.geometry instanceof THREE.Geometry) {
                  if (obj.geometry.faceVertexUvs.length > 0) {
                    hasUV = true;
                  }
                }
              }

              // object can have a MeshPhongMaterial as a material property
              // if so, we convert into MaterialPropertiesStandard
              let material = null;

              // there might be a material even if we have no input material data,
              // in that case we ignore this 'random' material
              if (materials != null && obj.hasOwnProperty('material')) {
                material = new MaterialPropertiesStandard();

                material.side = obj.material.side;
                material.color = obj.material.color;
                material.opacity = obj.material.opacity;
                material.transparent = obj.material.transparent;

                if (obj.material.hasOwnProperty('shininess')) {
                  material.metalness = Math.min(1, obj.material.shininess);
                  material.roughness = 1 - Math.min(1, obj.material.shininess);
                }

                if (obj.material.map != null) {
                  let m = obj.material.map.clone();
                  m.needsUpdate = true;
                  material.map = m;
                }

                // set other maps
                ['alpha', 'normal', 'bump'].forEach(function(attr) {
                  if (obj.material[attr + 'Map'] != null) {
                    let smap = obj.material[attr + 'Map'].clone();
                    smap.needsUpdate = true;
                    material[attr + 'Map'] = smap;
                  }
                });
              }

              let i = 0;
              for (let t of transforms) {
                let pathString = 'transformation_' + i;
                let geometryDefinition = {
                  path: pathString,
                  geometry: obj.geometry,
                  texCoord: hasUV,
                  initialMatrix: t,
                  type: 'mesh'
                };
                if (material != null) {
                  geometryDefinition.material = material;
                }
                outputGeometry.push(geometryDefinition);
                ++i;
              }
            }
          });
          resolve(outputGeometry);
        }, onProgress, onError );
      });
    };

    let haveMtl = urlMtl != null && urlMtl.length > 0;

    if (haveMtl) {
      return loadMtl;
    }
    return loadObj();
  };
};

module.exports = OBJLoader;
